import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Button } from '@mui/material';
import { DataGrid, GridPaginationModel, GridSortDirection, GridSortModel } from '@mui/x-data-grid';
import type { GridColDef } from '@mui/x-data-grid/models/colDef/gridColDef';
import { AxiosError } from 'axios';
import { IOption } from 'components/elements/CustomSelect';
import toastService from 'components/elements/Toastify';
import { ManageCityModal } from 'components/modules/ManageCityModal';
import { RejectedDataProcessButton } from 'components/modules/RejectedDataProcessButton';
import { UpdateCityAnchor } from 'components/modules/UpdateCityAnchor';
import { UtilityMapProcessingWarning } from 'components/modules/UtilityMapProcessingWarning';
import { ROUTES } from 'routes';
import { ICitiesListResponse, ICityFormValues, ICityItem, IErrorResponse, IQuery, IRegionItem } from 'types';
import { PAGINATION_LIMIT } from 'utils/constants';
import { CITIES_FIELDS_NAME, SORT_DIR } from 'utils/enums';
import { Rights } from 'utils/enums/rights.enum';
import { formatQueryString, getErrorMessage, isUtilityMapProcessRunning, parseQueryString } from 'utils/helpers';
import { getTableSetting } from 'utils/helpers/table-setting';
import { useModal, useUtilityMapStatus } from 'utils/hooks';
import { usePermissions } from 'utils/hooks/use-permissions';
import { CitiesService } from 'utils/services/cities.service';
import { RejectedDataService } from 'utils/services/rejected.service';
import { RegionsService } from 'utils/services/regions.service';
import { COLUMNS_CONFIG, ICityTableRow } from './config';

const DEVICE_FP = '6037e65eefbdasdfadsfasdf3';

interface ICityPrefixOptions {
	prefixes: IOption[];
	shortPrefixes: IOption[];
}

let cachedCityPrefixOptions: ICityPrefixOptions | null = null;
let cityPrefixOptionsRequest: Promise<ICityPrefixOptions> | null = null;

const toOptions = (values: string[]): IOption[] => values.map((value) => ({ title: value, value }));

const getCityPrefixOptions = async (): Promise<ICityPrefixOptions> => {
	if (cachedCityPrefixOptions) {
		return cachedCityPrefixOptions;
	}

	if (!cityPrefixOptionsRequest) {
		cityPrefixOptionsRequest = Promise.all([
			CitiesService.getCityPrefixes({ deviceFP: DEVICE_FP }),
			CitiesService.getCityShortPrefixes({ deviceFP: DEVICE_FP }),
		])
			.then(([prefixesResponse, shortPrefixesResponse]) => ({
				prefixes: toOptions(prefixesResponse.data.data),
				shortPrefixes: toOptions(shortPrefixesResponse.data.data),
			}))
			.then((options) => {
				cachedCityPrefixOptions = options;
				return options;
			})
			.finally(() => {
				cityPrefixOptionsRequest = null;
			});
	}

	return cityPrefixOptionsRequest;
};

const INITIAL_VALUES: ICityFormValues = {
	name: '',
	shorPrefix: '',
	prefix: '',
	regionId: '',
};

const normalizeCitiesResponse = (responseData: ICitiesListResponse): { results: ICityItem[]; total: number } => {
	if (Array.isArray(responseData)) {
		return {
			results: responseData,
			total: responseData.length,
		};
	}

	return {
		results: responseData.results || [],
		total: responseData.total || 0,
	};
};

export const CitiesPage: FC = () => {
	const { search } = useLocation();
	const navigate = useNavigate();
	const [state, setState] = useState<{ cities: ICityItem[]; total: number; isLoading: boolean }>({
		cities: [],
		total: 0,
		isLoading: false,
	});
	const [regionOptions, setRegionOptions] = useState<IOption[]>([]);
	const [prefixOptions, setPrefixOptions] = useState<ICityPrefixOptions>({
		prefixes: cachedCityPrefixOptions?.prefixes || [],
		shortPrefixes: cachedCityPrefixOptions?.shortPrefixes || [],
	});
	const [isRegionOptionsLoading, setIsRegionOptionsLoading] = useState(false);
	const [columnWidths, setColumnWidths] = useState({});
	const [isCreateModalOpen, handleOpenCreateModal, handleCloseCreateModal] = useModal();
	const { markRunning: markUtilityMapRunning, status: utilityMapStatus } = useUtilityMapStatus('byCity');
	const checkRights = usePermissions();

	const query = useMemo(() => parseQueryString<IQuery>(search, { parseBooleans: true, parseNumbers: true }), [search]);

	const requestBody = useMemo(
		() => ({
			offset: (query.page || 0) * (query.pageSize || PAGINATION_LIMIT.SMALL),
			limit: query.pageSize || PAGINATION_LIMIT.SMALL,
			orderBy: [
				{
					field: query.field || CITIES_FIELDS_NAME.NAME,
					direction: query.sort || SORT_DIR.ASC,
				},
			],
			searchBy: {
				name: query.searchQuery,
			},
			deviceFP: DEVICE_FP,
		}),
		[query]
	);

	const loadCities = useCallback(async (): Promise<void> => {
		setState((prevState) => ({ ...prevState, isLoading: true }));

		try {
			const response = await CitiesService.getCities(requestBody);
			const normalizedResponse = normalizeCitiesResponse(response.data);
			setState({
				cities: normalizedResponse.results,
				total: normalizedResponse.total,
				isLoading: false,
			});
		} catch (error) {
			setState((prevState) => ({ ...prevState, isLoading: false }));
			toastService.error(getErrorMessage(error as AxiosError<IErrorResponse>));
		}
	}, [requestBody]);

	useEffect(() => {
		void loadCities();
	}, [loadCities]);

	useEffect(() => {
		void getCityPrefixOptions()
			.then(setPrefixOptions)
			.catch((error) => {
				toastService.error(getErrorMessage(error as AxiosError<IErrorResponse>));
			});
	}, []);

	useEffect(() => {
		setColumnWidths(getTableSetting(ROUTES.cities));
	}, [requestBody]);

	const handleSearchRegions = useCallback(async (searchValue: string): Promise<void> => {
		setIsRegionOptionsLoading(true);

		try {
			const response = await RegionsService.getRegions({
				offset: 0,
				limit: 30,
				orderBy: [
					{
						field: 'id',
						direction: SORT_DIR.ASC,
					},
				],
				searchBy: {
					name: searchValue.trim(),
				},
				deviceFP: DEVICE_FP,
			});

			setRegionOptions(
				response.data.results.map((region: IRegionItem) => ({
					title: region.name,
					value: region.id,
				}))
			);
		} catch (error) {
			toastService.error(getErrorMessage(error as AxiosError<IErrorResponse>));
		} finally {
			setIsRegionOptionsLoading(false);
		}
	}, []);

	const handleOpenCreateCityModal = () => {
		void handleSearchRegions('');
		handleOpenCreateModal();
	};

	COLUMNS_CONFIG.forEach((item: GridColDef) => {
		// @ts-ignore
		if (columnWidths && columnWidths[item.field]) {
			// @ts-ignore
			item.width = columnWidths[item.field];
			item.flex = undefined;
		}
	});

	const handleColumnResize = (params: any) => {
		const newWidths = {
			...columnWidths,
			[params.colDef.field]: params.width,
		};
		setColumnWidths(newWidths);
		localStorage.setItem(ROUTES.cities, JSON.stringify(newWidths));
	};

	const handleChangePage = ({ page, pageSize }: GridPaginationModel): void => {
		navigate(
			{
				search: formatQueryString({
					...query,
					page,
					pageSize,
				}),
			},
			{ replace: true }
		);
	};

	const handleSortField = (model: GridSortModel): void => {
		const { field, sort } = model[0] || {};
		navigate(
			{
				search: formatQueryString({
					...query,
					field,
					sort,
					page: 0,
				}),
			},
			{ replace: true }
		);
	};

	const handleCreateCity = async (values: ICityFormValues) => {
		try {
			await CitiesService.createCity({
				name: values.name.trim(),
				shorPrefix: values.shorPrefix.trim(),
				prefix: values.prefix.trim(),
				regionId: values.regionId,
				deviceFP: DEVICE_FP,
			});
			toastService.success('Місто успішно створено');
			handleCloseCreateModal();
			await loadCities();
		} catch (error) {
			toastService.error(getErrorMessage(error as AxiosError<IErrorResponse>));
		}
	};

	const rows = useMemo<ICityTableRow[]>(
		() =>
			state.cities.map((city) => ({
				...city,
				'region.name': city.region?.name || '',
			})),
		[state.cities]
	);

	const columns = useMemo<GridColDef<ICityTableRow>[]>(
		() =>
			checkRights(Rights.STREETS_MANAGE)
				? [
						...COLUMNS_CONFIG,
						{
							field: 'processRejectedData',
							headerName: 'Rejected',
							sortable: false,
							align: 'center',
							headerAlign: 'center',
							width: 220,
							renderCell: (params) => (
								<RejectedDataProcessButton
									confirmTitle={`Запустити обробку rejected для міста ${params.row.name}?`}
									confirmMessage="Буде запущено фонову обробку rejected-даних для цього міста."
									infoMessage="Запуск виконано. Перевірте через 2 години"
									disabled={!utilityMapStatus || isUtilityMapProcessRunning(utilityMapStatus)}
									onProcess={() => RejectedDataService.mapRejectedDataByCityId({ cityId: params.row.id })}
									onProcessSuccess={() => {
										markUtilityMapRunning('byCity');
									}}
								/>
							),
						},
						{
							field: 'edit',
							headerName: ' ',
							sortable: false,
							align: 'center',
							width: 60,
							renderCell: (params) => (
								<UpdateCityAnchor
									cityRow={params.row}
									onSuccess={loadCities}
									regionOptions={regionOptions}
									regionOptionsLoading={isRegionOptionsLoading}
									onRegionSearch={handleSearchRegions}
									prefixOptions={prefixOptions.prefixes}
									shortPrefixOptions={prefixOptions.shortPrefixes}
								/>
							),
						},
				  ]
				: COLUMNS_CONFIG,
		[
			checkRights,
			handleSearchRegions,
			isRegionOptionsLoading,
			loadCities,
			prefixOptions,
			markUtilityMapRunning,
			regionOptions,
			utilityMapStatus,
		]
	);

	return (
		<Box sx={{ display: 'grid', gap: 2, height: '100%' }}>
			<UtilityMapProcessingWarning status={utilityMapStatus} />
			{checkRights(Rights.STREETS_MANAGE) && (
				<Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
					<Button variant="contained" onClick={handleOpenCreateCityModal}>
						Створити
					</Button>
				</Box>
			)}

			<Box sx={{ minHeight: 420, height: '100%' }}>
				<DataGrid
					onColumnResize={handleColumnResize}
					disableColumnFilter
					disableRowSelectionOnClick
					rows={rows}
					columns={columns}
					rowCount={state.total}
					loading={state.isLoading}
					getRowId={(row) => row.id}
					initialState={{
						pagination: {
							paginationModel: {
								page: query.page || 0,
								pageSize: query.pageSize || PAGINATION_LIMIT.SMALL,
							},
						},
						sorting: {
							sortModel: [{ field: query.field || CITIES_FIELDS_NAME.NAME, sort: query.sort as GridSortDirection }],
						},
					}}
					slotProps={{
						pagination: {
							labelRowsPerPage: 'Показувати :',
							page: query.page || 0,
							rowsPerPage: query.pageSize || PAGINATION_LIMIT.SMALL,
						},
					}}
					paginationMode="server"
					sortingMode="server"
					onSortModelChange={handleSortField}
					onPaginationModelChange={handleChangePage}
					pageSizeOptions={[PAGINATION_LIMIT.SMALL, PAGINATION_LIMIT.MIDDLE, PAGINATION_LIMIT.BIG]}
				/>
			</Box>

			<ManageCityModal
				title="Створити місто"
				isOpen={isCreateModalOpen}
				onClose={handleCloseCreateModal}
				initialValues={INITIAL_VALUES}
				onSubmit={handleCreateCity}
				buttonText="Створити"
				regionOptions={regionOptions}
				regionOptionsLoading={isRegionOptionsLoading}
				onRegionSearch={handleSearchRegions}
				prefixOptions={prefixOptions.prefixes}
				shortPrefixOptions={prefixOptions.shortPrefixes}
			/>
		</Box>
	);
};
